import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findPageIframe } from './framingLive';

// EDITION DIRECTE DU TITRE, DU SOUS-TITRE ET DE LA DATE SUR LA COUVERTURE
// (2026-10-12, maquette « Atelier Bookipix »).
//
// La couverture est un document HTML rendu par le serveur (iframe) : le client ne
// connait pas la position des textes, qui change avec chacun des 6 styles. On la
// LIT dans l'apercu (.cvr-title / .cvr-subtitle / .cvr-date), on pose par-dessus une
// zone cliquable, et un clic ouvre un champ a l'endroit du texte. Pendant la saisie,
// le texte de l'apercu est mis a jour directement (retour immediat) ; la
// modification est envoyee au store a chaque frappe, comme dans le panneau (meme
// historique, meme sauvegarde). Entree ferme le champ, Echap annule.
//
// Sous-titre et date absents (rien n'est affiche tant qu'ils sont vides) : de petites
// pastilles « + Sous-titre » / « + Date » sous le titre permettent de les ajouter.
const FIELDS = [
  { key: 'title', selector: '.cvr-title', prop: 'title' },
  { key: 'subtitle', selector: '.cvr-subtitle', prop: 'subtitle' },
  { key: 'date', selector: '.cvr-date', prop: 'dateLabel' }
];
const MAX_LENGTH = { title: 180, subtitle: 220, date: 20 };
const POLL_MS = 500;

function readBoxes(root) {
  // L'iframe de la couverture (le calque est dans un hote voisin, pas dans la page elle-meme) ;
  // les positions se comptent depuis le coin du calque, qui recouvre exactement la page.
  const iframe = findPageIframe({ face: 'front' });
  let doc = null;
  try { doc = iframe && iframe.contentDocument; } catch (_error) { return null; }
  if (!root || !doc || !doc.body) return null;
  const pageRect = root.getBoundingClientRect();
  const frameRect = iframe.getBoundingClientRect();
  const scale = frameRect.width / (iframe.offsetWidth || 1);
  const win = iframe.contentWindow;
  const boxes = { pageWidth: pageRect.width, pageHeight: pageRect.height };
  FIELDS.forEach(({ key, selector }) => {
    const el = doc.querySelector(selector);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return;
    const style = win.getComputedStyle(el);
    boxes[key] = {
      left: Math.round(frameRect.left - pageRect.left + rect.left * scale),
      top: Math.round(frameRect.top - pageRect.top + rect.top * scale),
      width: Math.round(rect.width * scale),
      height: Math.round(rect.height * scale),
      fontSize: Math.round(parseFloat(style.fontSize) * scale * 10) / 10,
      fontFamily: style.fontFamily,
      fontStyle: style.fontStyle,
      fontWeight: style.fontWeight,
      letterSpacing: style.letterSpacing,
      text: el.textContent || ''
    };
  });
  return boxes;
}

// Zones de clic du titre / sous-titre / date : au moins 12 px entre deux zones, jamais l'une sur l'autre
// (correctif 2). Chaque zone deborde un peu du texte pour etre facile a viser ; si deux textes sont trop
// proches, on rogne les deux zones voisines a parts egales.
const MIN_GAP_PX = 12;
function separateHitBoxes(boxes) {
  const rects = {};
  FIELDS.forEach(({ key }) => {
    const b = boxes[key];
    if (b) rects[key] = { left: b.left - 4, top: b.top - 3, width: b.width + 8, height: b.height + 6 };
  });
  const keys = Object.keys(rects).sort((a, b) => rects[a].top - rects[b].top);
  for (let i = 0; i < keys.length - 1; i += 1) {
    const upper = rects[keys[i]];
    const lower = rects[keys[i + 1]];
    const gap = lower.top - (upper.top + upper.height);
    if (gap < MIN_GAP_PX) {
      const deficit = MIN_GAP_PX - gap;
      const cutUpper = Math.min(deficit / 2, Math.max(0, upper.height - 12));
      const cutLower = Math.min(deficit - cutUpper, Math.max(0, lower.height - 12));
      upper.height -= cutUpper;
      lower.top += cutLower;
      lower.height -= cutLower;
    }
  }
  return rects;
}

function CoverTextOverlay({ html, values, onChange }) {
  const { t } = useTranslation('atelier');
  const rootRef = useRef(null);
  const [boxes, setBoxes] = useState(null);
  // { key, original } : champ en cours de saisie.
  const [editing, setEditing] = useState(null);
  const editorRef = useRef(null);

  const measure = useCallback(() => {
    const next = readBoxes(rootRef.current);
    setBoxes((previous) => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next));
  }, []);

  // L'apercu est remplace apres chaque modification (double tampon) : on remesure peu
  // apres, et regulierement tant que la couverture est affichee (redimensionnement,
  // changement de style, chargement des polices).
  useEffect(() => {
    measure();
    const timer = setInterval(measure, POLL_MS);
    window.addEventListener('resize', measure);
    return () => { clearInterval(timer); window.removeEventListener('resize', measure); };
  }, [html, measure]);

  useLayoutEffect(() => {
    if (editing && editorRef.current) {
      editorRef.current.focus();
      editorRef.current.select();
    }
  }, [editing && editing.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const valueOf = (key) => {
    if (key === 'title') return values.title || '';
    if (key === 'subtitle') return values.subtitle || '';
    return values.dateLabel || (boxes && boxes.date ? boxes.date.text : '');
  };
  const propOf = (key) => FIELDS.find((field) => field.key === key).prop;

  // Retour immediat dans l'apercu pendant la saisie (l'apercu serveur suit juste apres).
  const mirrorToPreview = (key, value) => {
    const iframe = findPageIframe({ face: 'front' });
    try {
      const el = iframe && iframe.contentDocument && iframe.contentDocument.querySelector(FIELDS.find((field) => field.key === key).selector);
      if (el) el.textContent = value || (key === 'title' ? t('coverText.untitled') : '');
    } catch (_error) { /* apercu pas encore pret */ }
  };

  const open = (key) => setEditing({ key, original: valueOf(key) });
  // Bouton « Textes » de la barre au-dessus de la page : ouvre le titre.
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    const onRequest = () => openRef.current('title');
    window.addEventListener('atelier:cover-edit-text', onRequest);
    return () => window.removeEventListener('atelier:cover-edit-text', onRequest);
  }, []);
  const close = () => setEditing(null);
  const cancel = () => {
    if (!editing) return;
    onChange(propOf(editing.key), editing.original);
    mirrorToPreview(editing.key, editing.original);
    close();
  };

  if (!boxes) return <div ref={rootRef} className="atelier-cover-text-overlay" />;

  const title = boxes.title;
  // Emplacement du champ : sur le texte, ou, s'il n'est pas encore affiche, juste sous le titre.
  const placement = (key) => {
    const own = boxes[key];
    const base = own || (title ? { ...title, top: title.top + title.height + 8, height: title.fontSize * 1.6 } : null);
    if (!base) return null;
    const width = Math.min(boxes.pageWidth - 16, Math.max(base.width + 28, boxes.pageWidth * 0.55));
    const centre = base.left + base.width / 2;
    const left = Math.max(8, Math.min(boxes.pageWidth - width - 8, centre - width / 2));
    return { base, left, width };
  };

  const editor = editing ? placement(editing.key) : null;
  const hitBoxes = separateHitBoxes(boxes);
  const addable = [
    ['subtitle', t('coverText.addSubtitle')],
    ['date', t('coverText.addDate')]
  ].filter(([key]) => !boxes[key] && !(editing && editing.key === key));

  return (
    <div ref={rootRef} className="atelier-cover-text-overlay" onClick={(event) => event.stopPropagation()}>
      {FIELDS.map(({ key }) => boxes[key] && !(editing && editing.key === key) && (
        <button
          key={key}
          type="button"
          className="atelier-cover-text-hit"
          style={hitBoxes[key]}
          aria-label={t(`coverText.edit.${key}`)}
          title={t(`coverText.edit.${key}`)}
          onClick={(event) => { event.stopPropagation(); open(key); }}
        />
      ))}

      {title && addable.length > 0 && !editing && (
        <div className="atelier-cover-text-add" style={{ left: title.left + title.width / 2, top: title.top + title.height + 8 }}>
          {addable.map(([key, label]) => (
            <button key={key} type="button" onClick={(event) => { event.stopPropagation(); open(key); }}>+ {label}</button>
          ))}
        </div>
      )}

      {editing && editor && (
        <textarea
          ref={editorRef}
          className="atelier-cover-text-editor"
          rows={1}
          value={valueOf(editing.key)}
          maxLength={MAX_LENGTH[editing.key]}
          aria-label={t(`coverText.edit.${editing.key}`)}
          placeholder={t(`coverText.placeholder.${editing.key}`)}
          style={{
            left: editor.left,
            top: Math.max(4, editor.base.top - 4),
            width: editor.width,
            height: Math.max(editor.base.height + 12, Math.min(34, editor.base.fontSize * 1.3) + 12),
            fontSize: Math.max(14, Math.min(26, editor.base.fontSize)),
            fontFamily: editor.base.fontFamily,
            fontStyle: editor.base.fontStyle,
            fontWeight: editor.base.fontWeight
          }}
          onChange={(event) => {
            const value = event.target.value.replace(/\n/g, ' ');
            onChange(propOf(editing.key), value);
            mirrorToPreview(editing.key, value);
          }}
          onBlur={close}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === 'Enter') { event.preventDefault(); close(); }
            if (event.key === 'Escape') { event.preventDefault(); cancel(); }
          }}
        />
      )}
    </div>
  );
}

export default CoverTextOverlay;
